import dayjs from 'dayjs'

const DAY_LABELS = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']

export function mondayOfWeek(ref) {
    const d = dayjs(ref)
    const dow = d.day()
    const toMon = dow === 0 ? -6 : 1 - dow
    return d.add(toMon, 'day').startOf('day')
}

export function getWeekBounds(weekOffset) {
    const mon = mondayOfWeek(dayjs().add(weekOffset, 'week'))
    const sun = mon.add(6, 'day')
    return {
        monday: mon,
        weekStart: mon.format('YYYY-MM-DD'),
        weekEnd: sun.format('YYYY-MM-DD'),
        startStr: mon.format('YYYY-MM-DD'),
        endStr: sun.format('YYYY-MM-DD')
    }
}

export function getWeekLabel(weekOffset) {
    const { startStr, endStr } = getWeekBounds(weekOffset)
    return `${startStr} — ${endStr}`
}

export function getMonthKeysForWeek(weekOffset) {
    const { monday } = getWeekBounds(weekOffset)
    const sun = monday.add(6, 'day')
    const y1 = monday.format('YYYY-MM')
    const y2 = sun.format('YYYY-MM')
    return y1 === y2 ? [y1] : [y1, y2]
}

export function computeWeekOffsetBounds(period) {
    if (!period || !period.periodStartTime || !period.periodFinishTime) return null
    const start = dayjs(period.periodStartTime)
    const end = dayjs(period.periodFinishTime)
    if (!start.isValid() || !end.isValid()) return null
    const thisMonday = mondayOfWeek(dayjs())
    const minMonday = mondayOfWeek(start)
    const maxMonday = mondayOfWeek(end)
    if (minMonday.isAfter(maxMonday)) return null
    const minOffset = minMonday.diff(thisMonday, 'week')
    const maxOffset = maxMonday.diff(thisMonday, 'week')
    return {
        minOffset: Math.min(minOffset, maxOffset),
        maxOffset: Math.max(minOffset, maxOffset)
    }
}

export function clampWeekOffset(offset, bounds) {
    if (!bounds) return offset
    if (offset < bounds.minOffset) return bounds.minOffset
    if (offset > bounds.maxOffset) return bounds.maxOffset
    return offset
}

export function mergeMyCourseScheduleByDate(parts) {
    const merged = {}
    for (const part of parts) {
        const d = normalizeOnlineScheduleData(part)
        Object.keys(d).forEach(dateKey => {
            if (!merged[dateKey]) merged[dateKey] = []
            if (Array.isArray(d[dateKey])) merged[dateKey].push(...d[dateKey])
        })
    }
    const out = {}
    Object.keys(merged).forEach(dateKey => {
        const seen = new Set()
        out[dateKey] = merged[dateKey].filter(item => {
            const id = item && item.id
            if (id == null) return true
            if (seen.has(id)) return false
            seen.add(id)
            return true
        })
    })
    return out
}

/** 统一成 YYYY-MM-DD */
function normalizeDateKey(key) {
    if (key == null || key === '') return null
    const s = String(key).trim()
    const m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
    if (m) {
        return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`
    }
    const d = dayjs(s)
    return d.isValid() ? d.format('YYYY-MM-DD') : null
}

function groupLessonsArrayByDate(list) {
    const out = {}
    ;(list || []).forEach(lesson => {
        if (!lesson || typeof lesson !== 'object') return
        const start =
            dayjsFromLessonTs(lesson.lessonsBeginTime) ||
            dayjs(lesson.startTime || lesson.beginTime || lesson.date || lesson.lessonDate)
        if (!start || !start.isValid()) return
        const nk = start.format('YYYY-MM-DD')
        if (!out[nk]) out[nk] = []
        out[nk].push(lesson)
    })
    return out
}

/**
 * myCourseSchedule 的 data：优先按日 map；兼容数组 / list / 单条对象 / 不补零日期
 */
export function normalizeOnlineScheduleData(data) {
    if (data == null) return {}
    if (Array.isArray(data)) return groupLessonsArrayByDate(data)

    if (typeof data !== 'object') return {}

    const nested = data.list || data.records || data.rows || data.courses || data.lessonList
    if (Array.isArray(nested)) return groupLessonsArrayByDate(nested)

    const out = {}
    let hasDateKey = false
    Object.keys(data).forEach(key => {
        const nk = normalizeDateKey(key)
        if (!nk) return
        hasDateKey = true
        const val = data[key]
        const arr = Array.isArray(val)
            ? val
            : val && typeof val === 'object'
                ? [val]
                : []
        if (!arr.length) return
        if (!out[nk]) out[nk] = []
        out[nk].push(...arr)
    })
    if (hasDateKey) return out

    // 整包是一条课次对象
    if (data.lessonsBeginTime != null || data.lessonsName != null || data.id != null) {
        return groupLessonsArrayByDate([data])
    }
    return {}
}

function coerceScheduleItemsPayload(data) {
    if (data == null) return []
    if (Array.isArray(data)) return data
    const raw =
        data.items ||
        data.itemList ||
        data.scheduleItems ||
        data.list ||
        data.records ||
        (Array.isArray(data.rows) ? data.rows : null)
    return Array.isArray(raw) ? raw : []
}

function publishedRowDateKey(row, weekMondayStr) {
    const r = row && typeof row === 'object' ? row : {}
    const stRaw = r.startTime || r.startDate || r.beginTime
    if (stRaw != null) {
        const st = typeof stRaw === 'string' ? stRaw.replace('T', ' ').split('.')[0] : stRaw
        const ds = dayjs(st)
        if (ds.isValid()) return ds.format('YYYY-MM-DD')
    }
    const dayIndex = Number(r.dayIndex != null ? r.dayIndex : r.day != null ? r.day : r.weekdayIndex)
    const ws =
        r.weekStart != null && String(r.weekStart).trim() !== ''
            ? String(r.weekStart)
            : weekMondayStr
    if (Number.isFinite(dayIndex) && dayIndex >= 1 && dayIndex <= 7 && ws) {
        return dayjs(ws)
            .startOf('day')
            .add(dayIndex - 1, 'day')
            .format('YYYY-MM-DD')
    }
    const etRaw = r.endTime || r.endDate || r.finishTime
    if (etRaw != null) {
        const et = typeof etRaw === 'string' ? etRaw.replace('T', ' ').split('.')[0] : etRaw
        const de = dayjs(et)
        if (de.isValid()) return de.format('YYYY-MM-DD')
    }
    return null
}

export function groupPublishedItemsByDate(data, weekBounds) {
    const root =
        data != null && typeof data === 'object' && !Array.isArray(data)
            ? data
            : { items: Array.isArray(data) ? data : [] }
    const items = coerceScheduleItemsPayload(root)
    const weekStart = weekBounds && weekBounds.weekStart
    const weekEnd = weekBounds && weekBounds.weekEnd
    const ws = weekStart ? dayjs(weekStart).startOf('day') : null
    const we = weekEnd ? dayjs(weekEnd).endOf('day') : null
    const out = {}
    items.forEach(row => {
        const dateKey = publishedRowDateKey(row, weekStart)
        if (!dateKey) return
        const d = dayjs(dateKey)
        if (ws && ws.isValid() && d.isBefore(ws, 'day')) return
        if (we && we.isValid() && d.isAfter(we, 'day')) return
        if (!out[dateKey]) out[dateKey] = []
        out[dateKey].push(row)
    })
    return out
}

function dayjsFromLessonTs(ts) {
    if (ts == null || ts === '') return null
    const n = Number(ts)
    if (!Number.isFinite(n)) return null
    return dayjs(n > 1e12 ? n : n * 1000)
}

function sortTsOffline(row) {
    const t = dayjs(row.startTime)
    return t.isValid() ? t.valueOf() : 0
}

function sortTsPublished(row) {
    const r = row && typeof row === 'object' ? row : {}
    const stRaw = r.startTime || r.startDate || r.beginTime
    if (stRaw == null) return 0
    const st = typeof stRaw === 'string' ? stRaw.replace('T', ' ').split('.')[0] : stRaw
    const t = dayjs(st)
    return t.isValid() ? t.valueOf() : 0
}

function formatVenue(venue, venueShort) {
    const ven = String(venue || '').trim()
    if (ven && ven !== '—') {
        if (ven === 'school' || ven.includes('校内') || ven.includes('校')) return '[校内]'
        if (ven === 'offsite' || ven.includes('校外') || ven.includes('基地')) return '[校外]'
        return `[${ven}]`
    }
    const short = String(venueShort || '').trim()
    if (!short) return ''
    if (short === '校') return '[校内]'
    if (short === '基') return '[校外]'
    return `[${short}]`
}

function parsePublishedRowExt(row) {
    const raw = row && (row.extJson || row.ext || row.extraJson)
    if (raw == null || raw === '') return {}
    if (typeof raw === 'object' && !Array.isArray(raw)) return raw
    try {
        return JSON.parse(String(raw))
    } catch (e) {
        return {}
    }
}

function parseCourseScheduleItemDetailData(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null
    const template = data.resourceTemplate
    if (!template || typeof template !== 'object') return null
    const item = data.item && typeof data.item === 'object' ? data.item : null
    return {
        ...template,
        courseName: template.courseName || (item && (item.courseTitle || item.courseName)) || ''
    }
}

function pickPublishedResourceFields(row) {
    const venueRaw = String((row && (row.venue || row.venueValue)) || '')
        .trim()
        .toLowerCase()
    let venueShort = ''
    if (venueRaw === 'school' || venueRaw.includes('校内') || venueRaw.includes('校')) venueShort = '校'
    else if (venueRaw === 'offsite' || venueRaw.includes('校外') || venueRaw.includes('基地')) venueShort = '基'
    return {
        courseName: String((row && (row.courseName || row.name)) || '').trim(),
        locationDetail: row && row.locationDetail != null ? String(row.locationDetail).trim() : '',
        venue: row && row.venue,
        venueShort
    }
}

function mapOnlineToEvent(lesson) {
    const isAsync = Number(lesson.lessonsPattern) === 1
    const start = dayjsFromLessonTs(lesson.lessonsBeginTime)
    const end = dayjsFromLessonTs(lesson.lessonsEndTime)
    let time = ''
    if (isAsync) {
        time = '可回看'
    } else if (start && start.isValid() && end && end.isValid()) {
        time = `${start.format('HH:mm')}-${end.format('HH:mm')}`
    } else if (start && start.isValid()) {
        time = start.format('HH:mm')
    }
    return {
        title: lesson.lessonsName || lesson.courseName || lesson.name || '未命名课程',
        time,
        className: isAsync ? '录播课' : '直播课',
        typeClass: isAsync ? 'ev-live' : ''
    }
}

function mapOfflineToEvent(row) {
    const start = dayjs(row.startTime)
    const end = dayjs(row.endTime)
    const time =
        start.isValid() && end.isValid()
            ? `${start.format('HH:mm')}-${end.format('HH:mm')}`
            : ''
    return {
        title: row.courseName || '未命名课程',
        time,
        className: row.locationRemark || '线下活动',
        typeClass: 'ev-offline'
    }
}

function mapPublishedToEvent(row, resourceMap) {
    const ext = parsePublishedRowExt(row)
    const startRaw = row.startTime || row.startDate || row.beginTime
    const endRaw = row.endTime || row.endDate || row.finishTime
    const start = dayjs(
        typeof startRaw === 'string' ? startRaw.replace('T', ' ').split('.')[0] : startRaw
    )
    const end = dayjs(
        typeof endRaw === 'string' ? endRaw.replace('T', ' ').split('.')[0] : endRaw
    )
    const time =
        start.isValid() && end.isValid()
            ? `${start.format('HH:mm')}-${end.format('HH:mm')}`
            : ''
    const itemId = row.id || row.scheduleItemId || row.itemId
    const resource =
        itemId != null && itemId !== '' && resourceMap
            ? resourceMap[String(itemId)]
            : null
    const title =
        row.courseTitle ||
        (resource && resource.courseName) ||
        row.courseName ||
        ext.title ||
        ext.shortName ||
        '未命名课程'
    const locationDetail = (resource && resource.locationDetail) || ''
    const venueMeta = formatVenue(
        (resource && resource.venue) || row.venue || ext.venue,
        (resource && resource.venueShort) || row.venueShort || ext.venueShort
    )
    return {
        title,
        time,
        className: locationDetail || venueMeta || '已发布排课',
        typeClass: 'ev-offline'
    }
}

export function buildWeekDayEvents({
    weekOffset,
    onlineMap,
    offlineMap,
    publishedMap,
    resourceMap
}) {
    const { monday } = getWeekBounds(weekOffset)
    const online = onlineMap || {}
    const offline = offlineMap || {}
    const published = publishedMap || {}
    return DAY_LABELS.map((label, i) => {
        const d = monday.add(i, 'day')
        const dateKey = d.format('YYYY-MM-DD')
        const offlineRows = Array.isArray(offline[dateKey]) ? offline[dateKey].slice() : []
        const onlineRows = Array.isArray(online[dateKey]) ? online[dateKey].slice() : []
        const publishedRows = Array.isArray(published[dateKey]) ? published[dateKey].slice() : []
        const merged = [
            ...offlineRows.map(row => ({ kind: 'offline', raw: row, sortTs: sortTsOffline(row) })),
            ...onlineRows.map(row => ({
                kind: 'online',
                raw: row,
                sortTs: (dayjsFromLessonTs(row.lessonsBeginTime) &&
                    dayjsFromLessonTs(row.lessonsBeginTime).valueOf()) ||
                    0
            })),
            ...publishedRows.map(row => ({
                kind: 'published',
                raw: row,
                sortTs: sortTsPublished(row)
            }))
        ]
        merged.sort((a, b) => a.sortTs - b.sortTs)
        const events = merged.map(({ kind, raw }) => {
            if (kind === 'offline') return mapOfflineToEvent(raw)
            if (kind === 'published') return mapPublishedToEvent(raw, resourceMap)
            return mapOnlineToEvent(raw)
        })
        return {
            key: `${dateKey}-${weekOffset}`,
            label,
            dayNum: d.date(),
            isWeekend: i >= 5,
            events
        }
    })
}

export function findWeekOffsetForDate(dateStr) {
    const target = mondayOfWeek(dayjs(dateStr))
    const thisMonday = mondayOfWeek(dayjs())
    return target.diff(thisMonday, 'week')
}

/** 当前周无课时，跳到离今天最近、且有数据的那一周 */
export function pickWeekOffsetWithData({
    onlineMap,
    offlineMap,
    publishedMap,
    bounds,
    currentOffset
}) {
    const currentDays = buildWeekDayEvents({
        weekOffset: currentOffset,
        onlineMap,
        offlineMap,
        publishedMap,
        resourceMap: {}
    })
    if (currentDays.some(d => d.events && d.events.length)) return currentOffset

    const dateKeys = [
        ...Object.keys(onlineMap || {}),
        ...Object.keys(offlineMap || {}),
        ...Object.keys(publishedMap || {})
    ]
        .map(normalizeDateKey)
        .filter(Boolean)
        .sort()
    if (!dateKeys.length) return currentOffset

    const today = dayjs().startOf('day')
    let best = dateKeys[0]
    let bestDiff = Math.abs(dayjs(best).diff(today, 'day'))
    dateKeys.forEach(k => {
        const diff = Math.abs(dayjs(k).diff(today, 'day'))
        if (diff < bestDiff) {
            best = k
            bestDiff = diff
        }
    })
    return clampWeekOffset(findWeekOffsetForDate(best), bounds)
}

/**
 * 拉取某一空间某一周课表（三接口合并）
 * @param {object} api $Api.Paired
 */
export async function fetchSpaceWeekSchedule(api, {
    projectId,
    spaceId,
    userId,
    weekOffset
}) {
    const empty = {
        onlineMap: {},
        offlineMap: {},
        publishedMap: {},
        resourceMap: {}
    }
    if (!api || !projectId || !spaceId) return empty

    const weekBounds = getWeekBounds(weekOffset)
    const months = getMonthKeysForWeek(weekOffset)

    const [offlineRes, publishedRes, ...onlineResults] = await Promise.all([
        api.getCoursesByTime({
            projectId,
            spaceId,
            time: weekBounds.weekStart
        }).catch(() => null),
        api.getPublishedCourseSchedule({
            projectId,
            spaceId,
            weekStart: weekBounds.weekStart
        }).catch(() => null),
        ...(userId
            ? months.map(month =>
                api
                    .getMyCourseSchedule({
                        projectId,
                        spxId: spaceId,
                        userId,
                        month
                    })
                    .catch(() => null)
            )
            : [])
    ])

    let offlineMap = {}
    if (offlineRes && offlineRes.data != null) {
        const raw = offlineRes.data
        if (typeof raw === 'object' && !Array.isArray(raw)) {
            // 按日 map，日期 key 归一化
            Object.keys(raw).forEach(key => {
                const nk = normalizeDateKey(key)
                if (!nk) return
                const val = raw[key]
                const arr = Array.isArray(val) ? val : val && typeof val === 'object' ? [val] : []
                if (!arr.length) return
                if (!offlineMap[nk]) offlineMap[nk] = []
                offlineMap[nk].push(...arr)
            })
        }
    }

    const onlineParts = []
    onlineResults.forEach(res => {
        if (!res) return
        // 兼容 code 为数字/字符串；data 为空对象也算有效
        const codeOk = res.code === 200 || res.code === '200' || res.code == null
        if (!codeOk) return
        if (res.data == null) return
        onlineParts.push(res.data)
    })
    const onlineMap = mergeMyCourseScheduleByDate(onlineParts)

    let publishedMap = {}
    const resourceMap = {}
    if (publishedRes && publishedRes.data != null) {
        publishedMap = groupPublishedItemsByDate(publishedRes.data, weekBounds)
        const itemIds = []
        Object.keys(publishedMap).forEach(dateKey => {
            ;(publishedMap[dateKey] || []).forEach(row => {
                const itemId = row && (row.id || row.scheduleItemId || row.itemId)
                if (itemId != null && itemId !== '') itemIds.push(itemId)
            })
        })
        const uniq = [...new Set(itemIds.map(String))]
        await Promise.all(
            uniq.map(async itemId => {
                try {
                    const res = await api.getCourseScheduleItemDetail({
                        itemId: Number.isFinite(Number(itemId)) ? Number(itemId) : itemId
                    })
                    const row =
                        res && res.data ? parseCourseScheduleItemDetailData(res.data) : null
                    if (row) resourceMap[String(itemId)] = pickPublishedResourceFields(row)
                } catch (e) {
                    /* ignore single item */
                }
            })
        )
    }

    return { onlineMap, offlineMap, publishedMap, resourceMap }
}

export async function fetchProjectSchedulePeriod(api, projectId) {
    if (!api || !projectId) return null
    try {
        const res = await api.getProjectDetailTime({ projectId })
        if (res && res.data) {
            return {
                periodStartTime: res.data.periodStartTime,
                periodFinishTime: res.data.periodFinishTime
            }
        }
    } catch (e) {
        /* ignore */
    }
    return null
}

export { DAY_LABELS }
